import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, Ban, CheckCircle2, Download, FileText, Loader2, MapPin,
    Pause, Play, Plus, Save, Trash2, Upload, X,
} from 'lucide-react';
import { Button } from '../Button';
import { CustomerRatingStars } from '../crm/CustomerRatingStars';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { formatCoordinates } from '../../services/geoCache';
import {
    Freelancer,
    FreelancerQualification,
    QUALI_STATUS_KLASSEN,
    QUALI_STATUS_LABELS,
    QUALI_TYPEN,
    QUALI_TYP_KURZ,
    QUALI_TYP_LABELS,
    QualiTyp,
    STATUS_LABELS,
    einsatzbereitschaft,
    formatSteuersatz,
    heuteIso,
    istPflichtQuali,
    qualiStatus,
    tageBisAblauf,
} from '../../services/freelancerLogic';
import {
    createQualification,
    deleteQualification,
    getQualificationDocumentUrl,
    removeQualificationDocument,
    updateFreelancer,
    updateQualification,
    uploadQualificationDocument,
    verorteFreelancer,
} from '../../services/freelancerService';
import {
    PortalKandidat,
    fetchPortalKandidaten,
    setzePortalZugang,
} from '../../services/freelancerPortalService';
import { FreelancerAssignmentsSection } from './FreelancerAssignmentsSection';

// Stammdaten und Nachweise EINES Freelancers (ERP/CRM-Plan, Abschnitt 7 —
// Phase D1, Admin-Sicht).
//
// Duenner Container (Standing Decision): jede Entscheidung — laeuft ein
// Nachweis ab, ist jemand einsatzbereit, wie heisst der Steuersatz — kommt
// fertig aus freelancerLogic.ts, jeder Schreibvorgang aus freelancerService.ts.
// Hier steht nur Formularzustand.

const inputClass =
    'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

interface Props {
    freelancer: Freelancer;
    qualis: FreelancerQualification[];
    onChange: (f: Freelancer) => void;
    onQualisChange: (qualis: FreelancerQualification[]) => void;
    onBack: () => void;
}

/** Cent <-> Eingabefeld. Leer bleibt leer (kein Satz hinterlegt), nicht 0. */
const centsZuFeld = (cents?: number): string =>
    Number.isFinite(cents as number) ? ((cents as number) / 100).toFixed(2).replace('.', ',') : '';

const feldZuCents = (text: string): number | undefined => {
    const roh = (text || '').trim().replace(/\./g, '').replace(',', '.');
    if (!roh) return undefined;
    const zahl = Number(roh);
    if (!Number.isFinite(zahl) || zahl < 0) return undefined;
    return Math.round(zahl * 100);
};

export const FreelancerDetail: React.FC<Props> = ({
    freelancer, qualis, onChange, onQualisChange, onBack,
}) => {
    const [draft, setDraft] = useState<Freelancer>(freelancer);
    const [satzFeld, setSatzFeld] = useState(centsZuFeld(freelancer.satzJeBegehungCents));
    const [speichert, setSpeichert] = useState(false);
    const [gespeichert, setGespeichert] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [verortet, setVerortet] = useState(false);

    // Neuer Nachweis
    const [neuTyp, setNeuTyp] = useState<QualiTyp>('rab30_b');
    const [neuBezeichnung, setNeuBezeichnung] = useState('');
    const [neuGueltigBis, setNeuGueltigBis] = useState('');
    const [legtAn, setLegtAn] = useState(false);

    // Laufende Datei-Operation je Nachweis (Upload/Download).
    const [busyQuali, setBusyQuali] = useState<string | null>(null);

    // Portal-Zugang (D3). Eigener Fehlerkanal: ein fehlgeschlagener
    // Kandidatenabruf (z. B. weil freelancer_portal.sql noch nicht gelaufen
    // ist) darf die Stammdatenpflege nicht mit einer roten Zeile blockieren.
    const [kandidaten, setKandidaten] = useState<PortalKandidat[]>([]);
    const [zugangAuswahl, setZugangAuswahl] = useState('');
    const [zugangBusy, setZugangBusy] = useState(false);
    const [zugangFehler, setZugangFehler] = useState<string | null>(null);

    useEffect(() => {
        setDraft(freelancer);
        setSatzFeld(centsZuFeld(freelancer.satzJeBegehungCents));
    }, [freelancer]);

    useEffect(() => {
        let abgebrochen = false;
        setZugangAuswahl('');
        fetchPortalKandidaten(freelancer.profileUserId)
            .then((k) => { if (!abgebrochen) setKandidaten(k); })
            .catch((e) => { if (!abgebrochen) setZugangFehler(rechtFehlerOder(e)); });
        return () => { abgebrochen = true; };
    }, [freelancer.id, freelancer.profileUserId]);

    const heute = heuteIso();
    const bereitschaft = useMemo(
        () => einsatzbereitschaft(freelancer, qualis, heute),
        [freelancer, qualis, heute],
    );

    // Verortung als Klartext (gleiche Anzeige wie im CustomerDetail).
    // '' heisst „noch nicht verortet" und ist vor der ersten Adresspflege
    // der Normalfall.
    const geoText = useMemo(
        () => formatCoordinates(freelancer.lat, freelancer.lng),
        [freelancer.lat, freelancer.lng],
    );

    const set = <K extends keyof Freelancer>(key: K, wert: Freelancer[K]) => {
        setDraft((d) => ({ ...d, [key]: wert }));
        setGespeichert(false);
    };

    const speichern = async () => {
        setFehler(null);
        setSpeichert(true);
        try {
            const aktualisiert = await updateFreelancer(freelancer.id, {
                name: draft.name.trim(),
                firma: draft.firma,
                street: draft.street,
                zip: draft.zip,
                city: draft.city,
                einsatzradiusKm: draft.einsatzradiusKm,
                satzJeBegehungCents: feldZuCents(satzFeld),
                steuersatzBp: draft.steuersatzBp,
                iban: draft.iban,
                ustIdnr: draft.ustIdnr,
                steuernummer: draft.steuernummer,
                datevKreditorNr: draft.datevKreditorNr,
                rating: draft.rating,
                notes: draft.notes,
            });
            if (aktualisiert) onChange(aktualisiert);
            setGespeichert(true);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setSpeichert(false);
        }
    };

    const statusSetzen = async (status: Freelancer['status']) => {
        setFehler(null);
        try {
            const aktualisiert = await updateFreelancer(freelancer.id, { status });
            if (aktualisiert) onChange(aktualisiert);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    /**
     * Portal-Zugang setzen oder loesen. `null` loest die Verknuepfung; der
     * Zugang ist danach sofort wirkungslos, weil jede Freelancer-Policy an
     * `profile_user_id = auth.uid()` haengt.
     */
    const zugangSetzen = async (profileUserId: string | null) => {
        setZugangFehler(null);
        setZugangBusy(true);
        try {
            const aktualisiert = await setzePortalZugang(freelancer.id, profileUserId || null);
            if (aktualisiert) onChange(aktualisiert);
        } catch (e) {
            setZugangFehler(rechtFehlerOder(e));
        } finally {
            setZugangBusy(false);
        }
    };

    const adresseVerorten = async () => {
        setFehler(null);
        setVerortet(true);
        try {
            const aktualisiert = await verorteFreelancer(freelancer);
            if (aktualisiert) onChange(aktualisiert);
            else setFehler('Die Adresse ließ sich nicht verorten. Bitte Schreibweise prüfen.');
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setVerortet(false);
        }
    };

    const nachweisAnlegen = async () => {
        setFehler(null);
        setLegtAn(true);
        try {
            const neu = await createQualification({
                freelancerId: freelancer.id,
                typ: neuTyp,
                bezeichnung: neuBezeichnung.trim() || undefined,
                gueltigBis: neuGueltigBis || undefined,
            });
            onQualisChange([...qualis, neu]);
            setNeuBezeichnung('');
            setNeuGueltigBis('');
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLegtAn(false);
        }
    };

    const ablaufAendern = async (q: FreelancerQualification, gueltigBis: string) => {
        setFehler(null);
        try {
            const aktualisiert = await updateQualification(q.id, { gueltigBis });
            if (aktualisiert) onQualisChange(qualis.map((x) => (x.id === q.id ? aktualisiert : x)));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const nachweisLoeschen = async (q: FreelancerQualification) => {
        setFehler(null);
        try {
            await deleteQualification(q);
            onQualisChange(qualis.filter((x) => x.id !== q.id));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const dokumentHochladen = async (q: FreelancerQualification, datei: File | null) => {
        if (!datei) return;
        setFehler(null);
        setBusyQuali(q.id);
        try {
            const aktualisiert = await uploadQualificationDocument(freelancer.id, q.id, datei);
            if (aktualisiert) onQualisChange(qualis.map((x) => (x.id === q.id ? aktualisiert : x)));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusyQuali(null);
        }
    };

    const dokumentOeffnen = async (q: FreelancerQualification) => {
        if (!q.dokumentPfad) return;
        setFehler(null);
        setBusyQuali(q.id);
        try {
            // Kurzlebige signierte URL (5 Minuten, freelancerService). Neues
            // Fenster mit noopener: der Nachweis liegt in einem privaten
            // Bucket, die URL soll nicht als Referrer weiterwandern.
            const url = await getQualificationDocumentUrl(q.dokumentPfad);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusyQuali(null);
        }
    };

    const dokumentEntfernen = async (q: FreelancerQualification) => {
        setFehler(null);
        setBusyQuali(q.id);
        try {
            const aktualisiert = await removeQualificationDocument(q);
            if (aktualisiert) onQualisChange(qualis.map((x) => (x.id === q.id ? aktualisiert : x)));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusyQuali(null);
        }
    };

    // Nachweise sortiert: Pflicht zuerst, darin nach Typ-Reihenfolge.
    const sortierteQualis = useMemo(
        () =>
            [...qualis].sort((a, b) => {
                const pa = istPflichtQuali(a.typ) ? 0 : 1;
                const pb = istPflichtQuali(b.typ) ? 0 : 1;
                if (pa !== pb) return pa - pb;
                return QUALI_TYPEN.indexOf(a.typ) - QUALI_TYPEN.indexOf(b.typ);
            }),
        [qualis],
    );

    return (
        <div data-glass-view="freelancer" className="h-full overflow-y-auto">
            <div className="space-y-6 max-w-5xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
                {/* ── Kopf ── */}
                <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                            <button
                                type="button"
                                onClick={onBack}
                                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"
                                aria-label="Zurück zur Liste"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="min-w-0">
                                <h2 className="text-xl font-bold text-slate-800 truncate">{freelancer.name}</h2>
                                <p className="text-xs text-slate-500 font-medium">
                                    {freelancer.firma || 'Einzelunternehmer'} ·{' '}
                                    {STATUS_LABELS[freelancer.status]}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                            {/* Statuswechsel. Der aktuelle Status ist deaktiviert —
                                „aktiv setzen", wenn schon aktiv, ist eine Falle. */}
                            <Button
                                variant="secondary"
                                onClick={() => statusSetzen('aktiv')}
                                disabled={freelancer.status === 'aktiv'}
                                icon={<Play className="w-4 h-4" />}
                                className="h-9 text-xs"
                            >
                                Aktiv
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => statusSetzen('pausiert')}
                                disabled={freelancer.status === 'pausiert'}
                                icon={<Pause className="w-4 h-4" />}
                                className="h-9 text-xs"
                                title="Vorübergehend nicht verfügbar"
                            >
                                Pausiert
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={() => statusSetzen('gesperrt')}
                                disabled={freelancer.status === 'gesperrt'}
                                icon={<Ban className="w-4 h-4" />}
                                className="h-9 text-xs"
                                title="Dauerhaft nicht mehr beauftragen"
                            >
                                Gesperrt
                            </Button>
                        </div>
                    </div>

                    {/* Einsatzbereitschaft — die Kernaussage der Seite. */}
                    <div
                        className={`mt-4 rounded-2xl border p-4 text-sm ${
                            bereitschaft.bereit
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                : 'bg-rose-50 border-rose-200 text-rose-800'
                        }`}
                    >
                        <p className="flex items-center gap-2 font-bold">
                            {bereitschaft.bereit ? (
                                <CheckCircle2 className="w-4 h-4 shrink-0" />
                            ) : (
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                            )}
                            {bereitschaft.bereit ? 'Einsatzbereit' : 'Blockiert für neue Zuweisungen'}
                        </p>
                        {bereitschaft.gruende.length > 0 && (
                            <ul className="mt-2 space-y-0.5 text-xs list-disc list-inside">
                                {bereitschaft.gruende.map((g) => (
                                    <li key={g}>{g}</li>
                                ))}
                            </ul>
                        )}
                        {bereitschaft.warnungen.length > 0 && (
                            <ul className="mt-2 space-y-0.5 text-xs text-amber-700 list-disc list-inside">
                                {bereitschaft.warnungen.map((w) => (
                                    <li key={w}>{w}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {fehler && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
                        <span className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            {fehler}
                        </span>
                        <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-rose-100 rounded p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* ── Stammdaten ── */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                    <h3 className="text-sm font-bold text-slate-700">Stammdaten</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Name</label>
                            <input value={draft.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Firma (optional)</label>
                            <input value={draft.firma || ''} onChange={(e) => set('firma', e.target.value)} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1.5 md:col-span-2">
                            <label className={labelClass}>Straße</label>
                            <input value={draft.street || ''} onChange={(e) => set('street', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>PLZ</label>
                            <input value={draft.zip || ''} onChange={(e) => set('zip', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Ort</label>
                            <input value={draft.city || ''} onChange={(e) => set('city', e.target.value)} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Verortung: Anzeige wie im CustomerDetail, hier aber mit
                            Knopf — es ist EINE Adresse und damit EINE Anfrage; der
                            Sammellauf des Kundenstamms hat hier kein Gegenstueck. */}
                        <div className="space-y-1.5">
                            <label className={labelClass}>Verortung</label>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500">
                                <MapPin className="w-3.5 h-3.5 shrink-0 text-slate-400" />
                                {geoText ? (
                                    <span className="font-mono text-slate-600">{geoText}</span>
                                ) : (
                                    <span>Noch nicht verortet</span>
                                )}
                                <button
                                    type="button"
                                    onClick={adresseVerorten}
                                    disabled={verortet}
                                    className="ml-auto text-[11px] font-bold text-blue-600 hover:underline disabled:opacity-50 shrink-0"
                                >
                                    {verortet ? 'sucht…' : geoText ? 'neu verorten' : 'verorten'}
                                </button>
                            </div>
                            <p className="text-[11px] text-slate-400 ml-1">
                                Auf der Karte wird die Position bewusst grob (~1 km) angezeigt.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <label className={labelClass}>Einsatzradius (km)</label>
                            <input
                                type="number"
                                min={1}
                                max={500}
                                value={draft.einsatzradiusKm}
                                onChange={(e) => set('einsatzradiusKm', Number(e.target.value))}
                                className={inputClass}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Satz je Begehung (€)</label>
                            <input
                                value={satzFeld}
                                onChange={(e) => { setSatzFeld(e.target.value); setGespeichert(false); }}
                                placeholder="z. B. 180,00"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Umsatzsteuer</label>
                            <select
                                value={draft.steuersatzBp}
                                onChange={(e) => set('steuersatzBp', Number(e.target.value))}
                                className={inputClass}
                            >
                                <option value={1900}>{formatSteuersatz(1900)}</option>
                                <option value={700}>{formatSteuersatz(700)}</option>
                                <option value={0}>{formatSteuersatz(0)}</option>
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Bewertung</label>
                            <div className="px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-2">
                                <CustomerRatingStars
                                    value={draft.rating || 0}
                                    onChange={(v) => set('rating', v)}
                                />
                                {draft.rating ? (
                                    <button
                                        type="button"
                                        onClick={() => set('rating', undefined)}
                                        className="ml-auto text-[11px] text-slate-400 hover:text-slate-600"
                                    >
                                        zurücksetzen
                                    </button>
                                ) : (
                                    <span className="ml-auto text-[11px] text-slate-400">noch keine</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1.5">
                            <label className={labelClass}>IBAN</label>
                            <input value={draft.iban || ''} onChange={(e) => set('iban', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>USt-IdNr.</label>
                            <input value={draft.ustIdnr || ''} onChange={(e) => set('ustIdnr', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Steuernummer</label>
                            <input value={draft.steuernummer || ''} onChange={(e) => set('steuernummer', e.target.value)} className={inputClass} />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>DATEV-Kreditor</label>
                            <input
                                value={draft.datevKreditorNr || ''}
                                onChange={(e) => set('datevKreditorNr', e.target.value)}
                                className={inputClass}
                                title="Kreditorenkonto für den Aufwandsbuchungssatz der Gutschriften (Phase D5)"
                            />
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className={labelClass}>Notizen</label>
                        <textarea
                            value={draft.notes || ''}
                            onChange={(e) => set('notes', e.target.value)}
                            rows={3}
                            className={inputClass}
                        />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                        <Button
                            variant="primary"
                            onClick={speichern}
                            disabled={speichert || !draft.name.trim()}
                            icon={speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        >
                            Speichern
                        </Button>
                        {gespeichert && (
                            <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Gespeichert
                            </span>
                        )}
                    </div>
                </section>

                {/* ── Portal-Zugang (Phase D3) ──
                    Der gefaehrlichste Schreibvorgang des Moduls: die falsche
                    Auswahl hier gibt einem fremden Menschen Zugriff auf die
                    Auftraege und Nachweise DIESES Freelancers. Deshalb eine
                    eigene Karte mit eigenem Knopf statt eines weiteren Feldes
                    im Stammdatenformular — und deshalb laesst
                    freelancerService.toFreelancerRow `profileUserId` bewusst
                    aus (Kommentar dort). */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                    <div>
                        <h3 className="text-sm font-bold text-slate-700">Portal-Zugang</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Verknüpft ein Benutzerkonto mit der Rolle „Freelancer" mit diesem Stammsatz.
                            Erst danach sieht die Person im Portal ihre Aufträge, ihre freigeschalteten
                            Projekte und ihre Nachweise — und ausschließlich diese.
                        </p>
                    </div>

                    {zugangFehler && (
                        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-xs flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="flex-1">{zugangFehler}</span>
                        </div>
                    )}

                    {freelancer.profileUserId ? (
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-700">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Portal-Zugang aktiv
                            </span>
                            <span className="text-xs text-slate-500 font-mono">
                                {freelancer.profileUserId}
                            </span>
                            <Button
                                variant="secondary"
                                onClick={() => zugangSetzen(null)}
                                disabled={zugangBusy}
                                className="text-xs py-1.5"
                                icon={zugangBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                            >
                                Zugang lösen
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-3">
                                <select
                                    value={zugangAuswahl}
                                    onChange={(e) => setZugangAuswahl(e.target.value)}
                                    className={`${inputClass} max-w-md`}
                                    disabled={zugangBusy || kandidaten.length === 0}
                                >
                                    <option value="">— Benutzerkonto wählen —</option>
                                    {kandidaten.map((k) => (
                                        <option key={k.id} value={k.id}>
                                            {k.name ? `${k.name} (${k.email})` : k.email}
                                        </option>
                                    ))}
                                </select>
                                <Button
                                    variant="primary"
                                    onClick={() => zugangSetzen(zugangAuswahl)}
                                    disabled={zugangBusy || !zugangAuswahl}
                                    className="text-xs py-1.5"
                                    icon={zugangBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                >
                                    Verknüpfen
                                </Button>
                            </div>
                            {kandidaten.length === 0 && (
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    Es gibt kein freies Benutzerkonto mit der Rolle „Freelancer". Der Weg
                                    dorthin führt über die Benutzerverwaltung: dort eine Einladung mit
                                    dieser Rolle verschicken (Verwaltung → Benutzer &amp; Rechte → Benutzer → Einladen). Sobald
                                    sich die Person registriert hat, erscheint sie hier zur Auswahl.
                                </p>
                            )}
                        </div>
                    )}
                </section>

                {/* ── Qualifikationsnachweise ── */}
                <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-4">
                    <div>
                        <h3 className="text-sm font-bold text-slate-700">Qualifikationsnachweise</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Pflicht sind RAB 30 Anlage B, RAB 30 Anlage C und eine laufende Haftpflicht.
                            Abgelaufene Pflichtnachweise blockieren neue Zuweisungen automatisch.
                        </p>
                    </div>

                    {sortierteQualis.length === 0 ? (
                        <p className="py-8 text-center text-sm text-slate-400 border-2 border-dashed border-slate-100 rounded-2xl">
                            Noch kein Nachweis erfasst.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                                        <th className="py-2 pr-3 font-bold">Nachweis</th>
                                        <th className="py-2 pr-3 font-bold">Bezeichnung</th>
                                        <th className="py-2 pr-3 font-bold">Gültig bis</th>
                                        <th className="py-2 pr-3 font-bold">Status</th>
                                        <th className="py-2 pr-3 font-bold">Dokument</th>
                                        <th className="py-2 font-bold text-right">Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortierteQualis.map((q) => {
                                        const status = qualiStatus(q, heute);
                                        const tage = tageBisAblauf(q.gueltigBis, heute);
                                        const busy = busyQuali === q.id;
                                        return (
                                            <tr key={q.id} className="border-b border-slate-50 last:border-0">
                                                <td className="py-2.5 pr-3">
                                                    <span className="font-bold text-slate-700">{QUALI_TYP_KURZ[q.typ]}</span>
                                                    {istPflichtQuali(q.typ) && (
                                                        <span className="ml-1.5 text-[10px] font-bold uppercase text-blue-600">Pflicht</span>
                                                    )}
                                                    <span className="block text-[11px] text-slate-400">{QUALI_TYP_LABELS[q.typ]}</span>
                                                </td>
                                                <td className="py-2.5 pr-3 text-slate-600">{q.bezeichnung || '—'}</td>
                                                <td className="py-2.5 pr-3">
                                                    <input
                                                        type="date"
                                                        value={q.gueltigBis || ''}
                                                        onChange={(e) => void ablaufAendern(q, e.target.value)}
                                                        className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                                                    />
                                                </td>
                                                <td className="py-2.5 pr-3">
                                                    {/* Ampel. Farben kommen aus der Logik — die
                                                        Oberflaeche waehlt hier nie selbst. */}
                                                    <span
                                                        className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-bold ${QUALI_STATUS_KLASSEN[status]}`}
                                                        title={
                                                            tage === null
                                                                ? 'Kein Ablaufdatum hinterlegt'
                                                                : tage < 0
                                                                  ? `Seit ${Math.abs(tage)} Tagen abgelaufen`
                                                                  : `Noch ${tage} Tage`
                                                        }
                                                    >
                                                        {QUALI_STATUS_LABELS[status]}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 pr-3">
                                                    {q.dokumentPfad ? (
                                                        <span className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => void dokumentOeffnen(q)}
                                                                disabled={busy}
                                                                className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:underline disabled:opacity-50"
                                                            >
                                                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                                                Öffnen
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => void dokumentEntfernen(q)}
                                                                disabled={busy}
                                                                className="text-[11px] text-slate-400 hover:text-rose-600 disabled:opacity-50"
                                                            >
                                                                entfernen
                                                            </button>
                                                        </span>
                                                    ) : (
                                                        <label className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 hover:text-blue-600 cursor-pointer">
                                                            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                                                            Hochladen
                                                            <input
                                                                type="file"
                                                                accept="application/pdf,image/jpeg,image/png"
                                                                className="hidden"
                                                                disabled={busy}
                                                                onChange={(e) => {
                                                                    void dokumentHochladen(q, e.target.files?.[0] ?? null);
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </label>
                                                    )}
                                                </td>
                                                <td className="py-2.5 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => void nachweisLoeschen(q)}
                                                        className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50"
                                                        aria-label={`${QUALI_TYP_KURZ[q.typ]} löschen`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Neuanlage */}
                    <div className="pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div className="space-y-1.5">
                            <label className={labelClass}>Art</label>
                            <select value={neuTyp} onChange={(e) => setNeuTyp(e.target.value as QualiTyp)} className={inputClass}>
                                {QUALI_TYPEN.map((t) => (
                                    <option key={t} value={t}>{QUALI_TYP_LABELS[t]}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Bezeichnung</label>
                            <input
                                value={neuBezeichnung}
                                onChange={(e) => setNeuBezeichnung(e.target.value)}
                                placeholder="z. B. Police-Nr. / Aussteller"
                                className={inputClass}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className={labelClass}>Gültig bis</label>
                            <input
                                type="date"
                                value={neuGueltigBis}
                                onChange={(e) => setNeuGueltigBis(e.target.value)}
                                className={inputClass}
                            />
                        </div>
                        <Button
                            variant="secondary"
                            onClick={nachweisAnlegen}
                            disabled={legtAn}
                            icon={legtAn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        >
                            Nachweis hinzufügen
                        </Button>
                    </div>

                    <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                        <FileText className="w-3 h-3 shrink-0" />
                        Dokumente liegen in einem privaten Speicher (PDF, JPG oder PNG, höchstens 10 MB) und
                        werden ausschließlich über kurzlebige signierte Links geöffnet.
                    </p>
                </section>

                {/* ── Aufträge & Projekte (Phase D2) ──
                    Eigene Komponente mit eigenem Ladezustand: die Akte selbst
                    bekommt Stammdaten und Nachweise von aussen gereicht, die
                    Auftragslage haengt aber an ganz anderen Tabellen und soll
                    die Akte nicht beim Oeffnen aufhalten. */}
                <FreelancerAssignmentsSection freelancerId={freelancer.id} />
            </div>
        </div>
    );
};
